import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  Upload,
  Button,
  message,
  UploadProps,
  Progress,
  Select,
  DatePicker,
  Tabs,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { toast } from "sonner";
import { RcFile } from "antd/es/upload";
import dayjs from "dayjs";
import type { TabsProps, UploadFile } from "antd";
import { customUploadRequest } from "@/util/axiosUpload";

interface DirectorsDetailsProps {
  onFormFinish?: (values: any) => void;
  initialValues?: any;
  onNext?: () => void;
  onPrevious?: () => void;
  isLastStep?: boolean;
}

const DirectorsDetails = ({
  initialValues,
  onNext,
  onPrevious,
  isLastStep,
  onFormFinish,
}: DirectorsDetailsProps) => {
  const [form] = Form.useForm();
  const [idFileList, setIdFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      const fieldsToSet = { ...initialValues };

      if (
        initialValues.directorDob &&
        typeof initialValues.directorDob === "string"
      ) {
        fieldsToSet.directorDob = dayjs(initialValues.directorDob);
      } else if (dayjs.isDayjs(initialValues.directorDob)) {
        fieldsToSet.directorDob = initialValues.directorDob;
      } else {
        fieldsToSet.directorDob = null;
      }

      if (initialValues.copyOfDirectorsId) {
        fieldsToSet.copyOfDirectorsId = initialValues.copyOfDirectorsId;

        if (
          !idFileList.length ||
          (idFileList.length &&
            idFileList[0]?.url !== initialValues.copyOfDirectorsId)
        ) {
          let fileName =
            initialValues.copyOfDirectorsId.split("/").pop() ||
            "director_id.pdf";

          // Truncate filename if longer than 20 characters
          if (fileName.length > 20) {
            fileName = fileName.substring(0, 20) + "qwertyuiopasdfg... .pdf";
          }

          setIdFileList([
            {
              uid: initialValues.copyOfDirectorsId,
              name: fileName,
              status: "done",
              url: initialValues.copyOfDirectorsId,
            },
          ]);
        }
      } else {
        fieldsToSet.copyOfDirectorsId = undefined;
        setIdFileList([]);
      }
      form.setFieldsValue(fieldsToSet);
    } else {
      form.resetFields();
      setIdFileList([]);
    }
  }, [initialValues, form]);
  

  const onFinish = (values: any) => {
    if (onFormFinish) {
      toast.success("Saved successfully");
      console.log(values);
      onFormFinish(values);
    }
  };

  const normUploadEvent = (e: any): string | undefined => {
    if (typeof e === 'string') return e;
    if (e?.file?.response?.path) return e.file.response.path;
    return undefined;
  };

  const beforeUpload = (file: RcFile) => {
    const isPdf = ["application/pdf"].includes(file.type);
    const isLt2M = file.size / 1024 / 1024 < 10;
    if (!isPdf) {
      toast.error("You can only upload PDF file!");
      return false; // This prevents the file from being added to the list
    }
    if (!isLt2M) {
      toast.error("File must smaller than 10MB!");
      return false; // This prevents the file from being added to the list
    }
    return isPdf && isLt2M;
  };

  const props: UploadProps = {
    name: "file",
    customRequest: customUploadRequest,
    fileList: idFileList,
    maxCount: 1,
    beforeUpload: beforeUpload,
    accept: ".pdf",
    onChange(info) {
      let newFileList = [...info.fileList];
      newFileList = newFileList.slice(-1);

      // Truncate filename if longer than 20 characters for display
      newFileList = newFileList.map((file) => {
        if (file.name && file.name.length > 20) {
          return {
            ...file,
            name: file.name.substring(0, 20) + "qwertyuiopasdfg... .pdf",
          };
        }
        return file;
      });

      setIdFileList(newFileList);

      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully`);
        form.setFieldsValue({
          copyOfDirectorsId: info.file.response.path,
        });
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
        setIdFileList([]);
      }
    },
    onRemove: () => {
      form.setFieldsValue({ copyOfDirectorsId: undefined });
      setIdFileList([]);
      return true;
    },
  };

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex flex-col w-full">
        <p className="text-sm">Step 5: Directors Details</p>
        <Progress
          steps={6}
          size={[80, 10]}
          strokeColor={["#1757a2", "#1757a2", "#1757a2", "#1757a2", "#1757a2"]}
          percent={80}
          showInfo={false}
          className="rounded-full"
          strokeLinecap="round"
          strokeWidth={1}
          style={{
            height: "6px",
            borderRadius: "999px",
          }}
        />
      </div>
      <Form
        className="w-full grid"
        layout="vertical"
        form={form}
        onFinish={onFinish}
      >
        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorFirstName"
            label="First Name"
            rules={[
              {
                required: true,
                message: "Please input the user's first name!",
              },
            ]}
            className="mb-1"
          >
            <Input size="large" placeholder="Amanda" />
          </Form.Item>
        </div>

        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorLastName"
            label="Last Name"
            rules={[
              {
                required: true,
                message: "Please input the user's last name!",
              },
            ]}
            className="mb-1"
          >
            <Input size="large" placeholder="Neli" />
          </Form.Item>
        </div>

        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorDob"
            label="Date of birth"
            rules={[
              {
                required: true,
                message: "Please enter user's date of birth!",
              },
            ]}
            className="mb-3"
          >
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
            />
          </Form.Item>
        </div>

        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorGender"
            label="Gender"
            rules={[
              {
                required: true,
                message: "Please select the user's gender!",
              },
            ]}
            className="mb-3"
          >
            <Select placeholder="Select user's gender" size="large">
              <Select.Option value="Male">Male</Select.Option>
              <Select.Option value="Female">Female</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>
        </div>

        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorEmail"
            label="Email"
            rules={[
              {
                required: true,
                message: "Please input the user's email!",
              },
              { type: "email", message: "Please input a valid email!" },
            ]}
            className="mb-1"
          >
            <Input size="large" placeholder="Amando@gmail.com" />
          </Form.Item>
        </div>

        <div className="col-12 md:col-6 mt-2">
          <Form.Item
            name="directorPhoneNo"
            label="Phone Number"
            rules={[
              {
                required: true,
                message: "Please enter user's phone number!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  // const phoneNumberPattern = /^(\+27|0)[6-8][0-9]{8}$/;
                  const phoneNumberPattern = /^\+?[1-9]\d{1,14}$/;
                  if (phoneNumberPattern.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Please enter a valid phoneNumber!"),
                  );
                },
              }),
            ]}
            className="mb-1"
          >
            <Input size="large" placeholder="+2763256392" />
          </Form.Item>
        </div>

        <div className="flex w-full mt-2 ml-2">
          <Form.Item
            name="copyOfDirectorsId"
            label="Upload copy of Directors Id"
            getValueFromEvent={normUploadEvent}
            rules={[{ required: true, message: "Please attach a copy of the Director's Id" }]}
          >
            <Upload {...props} beforeUpload={beforeUpload} accept=".pdf">
              <Button
                icon={<UploadOutlined />}
                className="bg-saferCity-primary  text-popupColor"
              >
                {idFileList.length > 0
                  ? "Change ID Document"
                  : "Upload ID Document"}
              </Button>
            </Upload>
          </Form.Item>
        </div>

        <div className="flex w-full">
          <Form.Item className="w-12 pt-2 mb-1">
            <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
              <Button
                type="primary"
                ghost
                onClick={onPrevious}
                size="large"
                className="w-3"
              >
                Back
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                className="w-3 bg-[#1757A2]"
              >
                Next
              </Button>
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default DirectorsDetails;
